<template>
  <div id="app" :class="{ 'game-mode': isGamePage }">
    
    <header class="mobile-header glassmorphism" v-if="isLoggedIn">
      <div class="header-left" @click="$router.push('/dashboard')">
        <img src="@/assets/logo.png" alt="Logo" class="header-logo" />
        <span class="app-title">SaltMate</span>
      </div>
      <div class="header-right">
        <router-link to="/salt-trader" class="mini-ticker" :class="priceClass">
          <i class="fas fa-chart-line"></i>
          <span>{{ saltPriceFormatted }}</span>
        </router-link>
        <router-link to="/notification" class="icon-btn">
          <i class="fas fa-bell"></i>
          <span class="badge-dot" v-if="hasNotification"></span>
        </router-link>
      </div>
    </header>

    <main class="main-content" :class="{ 'has-bottom-nav': isLoggedIn && !isGamePage }">
      <router-view v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </main>

    <transition name="slide-up">
      <div v-if="showNotice && currentNotice" class="floating-notice-bar" @click="openNoticeModal">
        <div class="notice-icon">
          <i class="fas fa-bullhorn"></i>
        </div>
        <div class="notice-content-wrapper">
          <span class="notice-tag">NOTICE</span>
          <div class="marquee-container">
             <p class="notice-msg scrolling-text">{{ currentNotice.message }}</p>
          </div>
        </div>
        <button class="notice-close" @click.stop="closeNotice">&times;</button>
      </div>
    </transition>

    <div v-if="isNoticeModalOpen && currentNotice" class="modal-overlay" @click.self="closeNoticeModal">
      <div class="modal-content notice-modal-content">
        <div class="notice-modal-header">
          <h3><i class="fas fa-bullhorn"></i> 알림</h3>
          <button @click="closeNoticeModal" class="close-btn">&times;</button>
        </div>
        <div class="notice-modal-body">
          <p class="full-message">{{ currentNotice.message }}</p>
          <div v-if="currentNotice.link" class="modal-actions">
            <button class="btn-go-link" @click="goToLink(currentNotice.link)">
              바로가기 <i class="fas fa-arrow-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <nav class="bottom-nav" v-if="isLoggedIn && !isGamePage">
      <router-link to="/dashboard" class="nav-item" active-class="active">
        <i class="fas fa-home"></i>
        <span>홈</span>
      </router-link>
      <router-link to="/game-zone" class="nav-item" active-class="active">
        <i class="fas fa-gamepad"></i>
        <span>게임존</span>
      </router-link>
      <div class="nav-item center-fab-wrapper">
        <router-link to="/plaza" class="center-fab">
          <i class="fas fa-cube"></i>
        </router-link>
        <span class="center-label">메타버스</span>
      </div>
      <router-link to="/my-assets" class="nav-item" active-class="active">
        <i class="fas fa-wallet"></i>
        <span>자산</span>
      </router-link>
      
      <div class="nav-item" @click="toggleMenuDrawer" :class="{ 'alert-active': hasPendingActivities }">
        <div class="menu-icon-wrapper">
          <i class="fas fa-bars"></i>
          <span class="alert-dot" v-if="hasPendingActivities"></span>
        </div>
        <span>전체메뉴</span>
      </div>
    </nav>

    <transition name="slide-right">
      <div v-if="isMenuOpen" class="menu-drawer">
        <div class="drawer-header">
          <div class="user-profile-top">
            <div class="avatar-circle"><i class="fas fa-user"></i></div>
            <div class="user-details">
              <span class="name">{{ userName }}님</span>
              <span class="role">{{ userRole === 'superAdmin' ? '관리자' : (isCenterManager ? '센터장' : '회원') }}</span>
            </div>
          </div>
          <button @click="toggleMenuDrawer" class="close-btn"><i class="fas fa-times"></i></button>
        </div>
        <div class="drawer-body">
          <ul class="menu-list">
             <li @click="navigateAndClose('/profile')"><i class="fas fa-id-card"></i> 내 프로필</li>
             <li @click="navigateAndClose('/mall')"><i class="fas fa-shopping-bag"></i> 솔트메이트 몰</li>
             <li v-if="isCenterManager" @click="openCenterQrModal" class="highlight-menu">
               <i class="fas fa-qrcode"></i> 센터 방문 인증 QR 생성
             </li>
             <li @click="navigateAndClose('/community')"><i class="fas fa-comments"></i> 커뮤니티</li>
             <li @click="navigateAndClose('/help')"><i class="fas fa-question-circle"></i> 고객센터</li>
             <hr>
             <li @click="logout" class="text-danger"><i class="fas fa-sign-out-alt"></i> 로그아웃</li>
          </ul>
        </div>

        <div class="drawer-footer-actions">
          <div v-if="activePvpCount > 0" class="action-card pvp-card" @click="navigateAndClose('/salt-pang-pvp')">
            <div class="card-icon"><i class="fas fa-fist-raised"></i></div>
            <div class="card-text">
              <span class="highlight">솔트팡 1:1 대전</span>
              <small>{{ activePvpCount }}명의 도전자가 대기 중!</small>
            </div>
            <button class="join-btn">승부하기</button>
          </div>

          <div class="action-card race-card" @click="enterFamilyRaceDirectly">
            <div class="race-pill">
              <span class="race-badge">NEXT</span>
              <span class="race-timer">{{ raceTimeDisplay }}</span>
            </div>
            <div class="card-text race-text">
              <span>가족 레이스 참여</span>
              <small><i class="fas fa-user"></i> {{ raceParticipants }}명 참여 중</small>
            </div>
          </div>
        </div>
      </div>
    </transition>
    <div v-if="isMenuOpen" class="drawer-overlay" @click="toggleMenuDrawer"></div>

    <div v-if="qrModal.visible" class="modal-overlay" @click.self="closeQrModal">
        <div class="modal-content">
            <header class="modal-header"><h3>방문 인증</h3><button @click="closeQrModal" class="close-button">&times;</button></header>
            <div class="modal-body"><p class="qr-info">QR코드를 인식하였습니다.</p></div>
        </div>
    </div>

    <div v-if="centerQrModal.visible" class="modal-overlay" @click.self="closeCenterQrModal">
      <div class="modal-content center-qr-content">
        <header class="modal-header"><h3>🏢 센터 방문 인증 QR</h3><button @click="closeCenterQrModal" class="close-button">&times;</button></header>
<div class="modal-body">
  <div v-if="centerQrModal.isLoading" class="qr-loading-placeholder">
    <i class="fas fa-circle-notch fa-spin"></i>
    <p>새로운 1회성 코드를 생성 중...</p>
  </div>

  <template v-else-if="centerQrModal.centerId">
    <div class="qr-wrapper">
      <qrcode-vue :value="centerQrModal.centerId" :size="250" level="H" />
    </div>
    <p class="guide-text">회원님이 이 QR을 스캔하면<br>방문 인증이 완료됩니다.</p>
    <div class="timer-text">보안 코드: {{ centerQrModal.centerId }}</div>
    <p style="font-size: 0.75rem; color: #e74c3c; margin-top: 5px;">* 5분간 유효한 1회성 코드입니다.</p>
  </template>
</div>
      </div>
    </div>

    <ChristmasPopup 
      :is-visible="isChristmasVisible" 
      @close="closeChristmas" 
    />

  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, reactive, provide } from "vue";
import { auth, db, rtdb } from "@/firebaseConfig"; 
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, onSnapshot, collection, query, where } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { ref as dbRef, onValue, set, onDisconnect, remove } from "firebase/database";
import { useRouter, useRoute } from "vue-router";
import QrcodeVue from 'qrcode.vue'; 

// [신규 추가] 크리스마스 팝업 컴포넌트 임포트
// import ChristmasPopup from '@/views/ChristmasPopup.vue';

const router = useRouter();
const route = useRoute();

// --- 실시간 자산 공유를 위한 전역 상태 ---
const userProfile = ref(null);
provide('userProfile', userProfile);

const isLoggedIn = ref(false);
const userName = ref("");
const userRole = ref(null);
const isCenterManager = ref(false); 
const isMenuOpen = ref(false); 
const hasNotification = ref(false); 

const raceTimeDisplay = ref("00:00");
const raceParticipants = ref(0);
const nextMatchTimestamp = ref(null);
const activePvpCount = ref(0);

const activeAnnouncements = ref([]);
const currentNotice = ref(null);
const showNotice = ref(false);
const isNoticeModalOpen = ref(false); 

// [신규 추가] 크리스마스 팝업 상태 관리
const isChristmasVisible = ref(false);
const closeChristmas = () => {
  isChristmasVisible.value = false;
};

let raceInterval = null;
let pvpUnsubscribe = null;
let noticeUnsubscribe = null;
let noticeInterval = null;
let raceDataUnsubscribe = null;
let userProfileUnsubscribe = null; 

const market = ref({ currentPrice: 0, priceHistory: [] });
const saltPrice = ref(0);
const qrModal = reactive({ visible: false, isLoading: false, qrId: null, error: null });
const centerQrModal = reactive({ visible: false, centerId: null, isLoading: false });

let saltPriceUnsubscribe = null;
let authUnsubscribe = null;
let presenceRef = null;

const isGamePage = computed(() => route.meta.isGamePage === true || route.path === '/plaza');
const saltPriceFormatted = computed(() => (saltPrice.value || 0).toFixed(2)); 
const priceChangeValue = computed(() => {
  const history = market.value?.priceHistory || [];
  if (history.length < 2) return 0;
  return saltPrice.value - (history[0]?.price || saltPrice.value);
});
const priceClass = computed(() => priceChangeValue.value > 0 ? 'up' : (priceChangeValue.value < 0 ? 'down' : ''));

const hasPendingActivities = computed(() => {
  return activePvpCount.value > 0 || raceParticipants.value > 10;
});

const toggleMenuDrawer = () => { isMenuOpen.value = !isMenuOpen.value; };
const navigateAndClose = (path) => { router.push(path); isMenuOpen.value = false; };
const enterFamilyRaceDirectly = () => { router.push({ path: '/salt-racing', query: { mode: 'family' } }); isMenuOpen.value = false; };
const openCenterQrModal = async () => {
  if (!auth.currentUser) return;

  // 1. 초기화 및 로딩 시작
  centerQrModal.centerId = null; 
  centerQrModal.isLoading = true;
  centerQrModal.visible = true;
  isMenuOpen.value = false;

  try {
    // 2. 서버의 'generateCenterQRCode' 함수 호출 준비
    const functionsInstance = getFunctions(undefined, "asia-northeast3");
    const generateFn = httpsCallable(functionsInstance, 'generateCenterQRCode');
    
    // 3. 서버 호출 (센터장의 경우 서버가 알아서 managedCenterId를 사용함)
    const result = await generateFn({ centerId: 'default' });
    
    if (result.data && result.data.success) {
      // 4. 서버가 준 1회성 qrId를 화면에 할당
      centerQrModal.centerId = result.data.qrId;
    }
  } catch (error) {
    console.error("QR 생성 실패:", error);
    alert("QR 코드를 생성할 권한이 없거나 오류가 발생했습니다.");
    centerQrModal.visible = false;
  } finally {
    centerQrModal.isLoading = false;
  }
};

const closeCenterQrModal = () => { centerQrModal.visible = false; };

const logout = async () => {
  try {
    isMenuOpen.value = false;
    await signOut(auth);
    router.push("/login");
  } catch (error) { console.error(error); }
};

const managePresence = (user) => {
  if (user) {
    presenceRef = dbRef(rtdb, `presence/${user.uid}`);
    const connectedRef = dbRef(rtdb, ".info/connected");
    onValue(connectedRef, (snap) => {
      if (snap.val() === true) {
        onDisconnect(presenceRef).remove();
        set(presenceRef, true);
      }
    });
  } else { if (presenceRef) { remove(presenceRef); presenceRef = null; } }
};

const listenToSaltPrice = () => {
  const marketRef = doc(db, "configuration", "saltMarket");
  saltPriceUnsubscribe = onSnapshot(marketRef, (docSnap) => {
    if (docSnap.exists()) {
      market.value = docSnap.data();
      saltPrice.value = market.value.currentPrice;
    }
  });
};

const listenToPvpMatches = () => {
  const q = query(collection(db, "matchmakingQueue"), where("status", "==", "waiting"));
  pvpUnsubscribe = onSnapshot(q, (snapshot) => { activePvpCount.value = snapshot.size; });
};

const listenToFamilyRaceInfo = () => {
  const raceDocRef = doc(db, "system", "saltRacingFamily");
  raceDataUnsubscribe = onSnapshot(raceDocRef, (docSnap) => {
    if (docSnap.exists()) {
      const data = docSnap.data();
      if (data.nextRaceTime) { nextMatchTimestamp.value = data.nextRaceTime.toDate().getTime(); }
      raceParticipants.value = data.totalBets || 0;
    }
  });
  raceInterval = setInterval(() => {
    if (!nextMatchTimestamp.value) return;
    const diff = nextMatchTimestamp.value - new Date().getTime();
    if (diff <= 0) { raceTimeDisplay.value = "진행 중"; } 
    else {
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      raceTimeDisplay.value = `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }
  }, 1000);
};

const startNoticeSystem = () => {
  const q = query(collection(db, "announcements"), where("isActive", "==", true));
  noticeUnsubscribe = onSnapshot(q, (snapshot) => { activeAnnouncements.value = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })); });
  const triggerNotice = () => {
    if (activeAnnouncements.value.length > 0 && !showNotice.value) {
      const randomIndex = Math.floor(Math.random() * activeAnnouncements.value.length);
      currentNotice.value = activeAnnouncements.value[randomIndex];
      showNotice.value = true;
      setTimeout(() => { showNotice.value = false; }, 10000);
    }
  };
  setTimeout(triggerNotice, 3000);
  noticeInterval = setInterval(triggerNotice, 300000);
};

const closeNotice = () => { showNotice.value = false; };
const openNoticeModal = () => { if (currentNotice.value) { isNoticeModalOpen.value = true; showNotice.value = false; } };
const closeNoticeModal = () => { isNoticeModalOpen.value = false; };
const goToLink = (link) => { router.push(link); closeNoticeModal(); };

const checkAuthState = () => {
  authUnsubscribe = onAuthStateChanged(auth, async (user) => {
    managePresence(user);
    if (userProfileUnsubscribe) { userProfileUnsubscribe(); } 

    if (user) {
      isLoggedIn.value = true;
      listenToSaltPrice(); listenToPvpMatches(); listenToFamilyRaceInfo(); startNoticeSystem(); 
      
      const userRef = doc(db, "users", user.uid);
      userProfileUnsubscribe = onSnapshot(userRef, (docSnap) => {
        if (docSnap.exists()) {
          const userData = docSnap.data();
          userProfile.value = { uid: user.uid, ...userData }; 
          userName.value = userData.name || "회원";
          userRole.value = userData.role || 'user';
          isCenterManager.value = userData.role === 'centerManager' || userData.isCenterOwner === true || userData.role === 'superAdmin'; 
        }
      });
    } else {
      isLoggedIn.value = false; userName.value = ""; userRole.value = null; isCenterManager.value = false; userProfile.value = null;
    }
  });
};

onMounted(() => { 
  checkAuthState();
  
  // [신규 추가] 앱 시작 0.5초 후 팝업 표시
  setTimeout(() => {
    isChristmasVisible.value = true;
  }, 500); 
});

onUnmounted(() => {
  if (authUnsubscribe) authUnsubscribe(); if (saltPriceUnsubscribe) saltPriceUnsubscribe();
  if (pvpUnsubscribe) pvpUnsubscribe(); if (noticeUnsubscribe) noticeUnsubscribe();
  if (raceDataUnsubscribe) raceDataUnsubscribe(); if (userProfileUnsubscribe) userProfileUnsubscribe();
  if (raceInterval) clearInterval(raceInterval); if (noticeInterval) clearInterval(noticeInterval);
});
</script>

<style scoped>
/* 기존 스타일 100% 동일 유지 */
.header-logo { height: 32px; width: auto; margin-right: 8px; cursor: pointer; }
#app { font-family: 'Noto Sans KR', sans-serif; background-color: #f5f7fa; min-height: 100vh; -webkit-font-smoothing: antialiased; }
.mobile-header { position: fixed; top: 0; left: 0; width: 100%; height: 60px; background: rgba(255,255,255,0.95); backdrop-filter: blur(10px); display: flex; justify-content: space-between; align-items: center; padding: 0 20px; z-index: 1000; box-shadow: 0 2px 10px rgba(0,0,0,0.05); }
.header-left { display: flex; align-items: center; cursor: pointer; }
.app-title { font-size: 1.3rem; font-weight: 800; color: #007bff; letter-spacing: -0.5px; }
.header-right { display: flex; align-items: center; gap: 15px; }
.mini-ticker { display: flex; align-items: center; gap: 5px; font-size: 0.9rem; font-weight: 700; color: #333; background: #f1f3f5; padding: 5px 12px; border-radius: 20px; text-decoration: none; }
.mini-ticker.up { color: #e74c3c; } .mini-ticker.down { color: #007bff; }
.icon-btn { font-size: 1.2rem; color: #333; position: relative; }
.badge-dot { position: absolute; top: 0; right: 0; width: 8px; height: 8px; background: #e74c3c; border-radius: 50%; }
.main-content { padding-top: 60px; min-height: 100vh; }
.main-content.has-bottom-nav { padding-bottom: 80px; }
.bottom-nav { position: fixed; bottom: 0; left: 0; width: 100%; height: 70px; background: #fff; display: flex; justify-content: space-around; align-items: center; box-shadow: 0 -2px 15px rgba(0,0,0,0.08); z-index: 1000; border-top-left-radius: 20px; border-top-right-radius: 20px; }
.nav-item { display: flex; flex-direction: column; align-items: center; justify-content: center; color: #868e96; text-decoration: none; font-size: 0.75rem; gap: 4px; width: 20%; cursor: pointer; position: relative; }
.nav-item i { font-size: 1.4rem; margin-bottom: 2px; transition: transform 0.2s; }
.nav-item.active { color: #007bff; font-weight: 700; }
.nav-item.active i { transform: translateY(-3px); }
.center-fab-wrapper { position: relative; top: -20px; }
.center-fab { width: 56px; height: 56px; background: linear-gradient(135deg, #007bff, #0056b3); border-radius: 50%; display: flex; justify-content: center; align-items: center; color: white; font-size: 1.5rem; box-shadow: 0 8px 20px rgba(0, 123, 255, 0.4); border: 4px solid #f5f7fa; }
.center-label { margin-top: 5px; color: #007bff; font-weight: 700; }
.menu-drawer { position: fixed; top: 0; right: 0; width: 80%; max-width: 300px; height: 100%; background: #fff; z-index: 2000; box-shadow: -2px 0 10px rgba(0,0,0,0.1); display: flex; flex-direction: column; }
.drawer-header { padding: 20px; background: #f8f9fa; border-bottom: 1px solid #eee; display: flex; justify-content: space-between; align-items: center; }
.user-profile-top { display: flex; align-items: center; gap: 10px; }
.avatar-circle { width: 40px; height: 40px; background: #e9ecef; border-radius: 50%; display: flex; justify-content: center; align-items: center; color: #adb5bd; }
.user-details { display: flex; flex-direction: column; }
.user-details .name { font-weight: 800; font-size: 1rem; }
.user-details .role { font-size: 0.8rem; color: #868e96; }
.close-btn { background: none; border: none; font-size: 1.2rem; cursor: pointer; color: #495057; }
.menu-list { list-style: none; padding: 20px; margin: 0; flex: 1; overflow-y: auto; }
.menu-list li { padding: 15px 0; font-size: 1rem; color: #333; border-bottom: 1px solid #f1f3f5; cursor: pointer; display: flex; align-items: center; gap: 10px; }
.menu-list li i { width: 24px; text-align: center; color: #adb5bd; }
.menu-list li:hover { color: #007bff; } .menu-list li:hover i { color: #007bff; } .text-danger { color: #e74c3c !important; }
.highlight-menu { background-color: #e3f2fd; color: #007bff !important; font-weight: bold; }
.highlight-menu i { color: #007bff !important; }
.nav-item.alert-active { color: #ff4757; }
.nav-item.alert-active i { animation: pulse-ring 1.5s infinite; }
.menu-icon-wrapper { position: relative; }
.alert-dot { position: absolute; top: -2px; right: -2px; width: 8px; height: 8px; background: #ff4757; border-radius: 50%; border: 1px solid white; }
@keyframes pulse-ring { 0% { transform: scale(1); } 50% { transform: scale(1.2); } 100% { transform: scale(1); } }
.drawer-footer-actions { background: #f8f9fa; padding: 15px; border-top: 1px solid #eee; display: flex; flex-direction: column; gap: 10px; }
.action-card { background: white; border-radius: 12px; padding: 12px 15px; display: flex; align-items: center; gap: 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.05); cursor: pointer; transition: transform 0.2s; }
.action-card:active { transform: scale(0.98); }
.pvp-card { border: 1px solid #ffeaa7; background: #fffdf0; }
.card-icon { width: 36px; height: 36px; background: #ff9f43; color: white; border-radius: 8px; display: flex; justify-content: center; align-items: center; font-size: 1.1rem; }
.card-text { flex: 1; display: flex; flex-direction: column; }
.card-text .highlight { font-weight: bold; color: #d35400; font-size: 0.95rem; }
.card-text small { color: #7f8c8d; font-size: 0.8rem; }
.join-btn { background: #ff9f43; color: white; border: none; padding: 5px 10px; border-radius: 20px; font-size: 0.8rem; font-weight: bold; }
.race-card { border: 1px solid #fab1a0; background: #fff5f2; }
.race-pill { background: #ff4757; color: white; border-radius: 20px; padding: 4px 10px; display: flex; align-items: center; gap: 5px; font-weight: bold; font-size: 0.85rem; }
.race-text { margin-left: 5px; }
.race-text span { font-weight: bold; color: #c0392b; font-size: 0.95rem; }
.drawer-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.5); z-index: 1500; }
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s; } .fade-enter-from, .fade-leave-to { opacity: 0; }
.slide-right-enter-active, .slide-right-leave-active { transition: transform 0.3s ease; } .slide-right-enter-from, .slide-right-leave-to { transform: translateX(100%); }
.modal-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.6); display: flex; justify-content: center; align-items: center; z-index: 3000; }
.modal-content { background: white; width: 90%; max-width: 350px; border-radius: 12px; overflow: hidden; }
.modal-header { padding: 15px; background: #f8f9fa; display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #eee; }
.modal-body { padding: 20px; display: flex; flex-direction: column; align-items: center; }
.qr-info { margin-top: 10px; color: #666; font-size: 0.9rem; } .qr-error { color: #e74c3c; }
.center-qr-content { text-align: center; }
.qr-wrapper { margin: 15px 0; border: 10px solid #f8f9fa; border-radius: 15px; display: inline-block; }
.guide-text { font-size: 1rem; color: #333; margin-bottom: 10px; line-height: 1.5; }
.timer-text { font-size: 0.8rem; color: #999; margin-top: 5px; }
.floating-notice-bar { position: fixed; bottom: 20px; left: 20px; right: 20px; background: rgba(44, 62, 80, 0.95); color: white; border-radius: 50px; padding: 12px 20px; display: flex; align-items: center; box-shadow: 0 5px 20px rgba(0,0,0,0.2); z-index: 1200; backdrop-filter: blur(5px); max-width: 600px; margin: 0 auto; cursor: pointer; }
.notice-icon { background: #f1c40f; width: 32px; height: 32px; border-radius: 50%; display: flex; justify-content: center; align-items: center; color: #333; margin-right: 12px; flex-shrink: 0; }
.notice-content-wrapper { flex: 1; overflow: hidden; display: flex; align-items: center; gap: 8px; }
.notice-tag { font-size: 0.65rem; background: #e74c3c; padding: 2px 6px; border-radius: 4px; font-weight: bold; white-space: nowrap; }
.marquee-container { flex: 1; overflow: hidden; white-space: nowrap; position: relative; mask-image: linear-gradient(to right, transparent, black 10%, black 90%, transparent); }
.scrolling-text { display: inline-block; padding-left: 100%; animation: marquee 10s linear infinite; font-size: 0.9rem; }
@keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-100%); } }
.notice-close { background: none; border: none; color: #aaa; font-size: 1.5rem; cursor: pointer; margin-left: 10px; }
.slide-up-enter-active, .slide-up-leave-active { transition: all 0.5s ease; }
.slide-up-enter-from, .slide-up-leave-to { transform: translateY(100px); opacity: 0; }
.notice-modal-content { max-width: 400px; }
.notice-modal-header { padding: 15px 20px; background: #f8f9fa; border-bottom: 1px solid #eee; display: flex; justify-content: space-between; align-items: center; }
.notice-modal-header h3 { margin: 0; font-size: 1.1rem; color: #333; }
.notice-modal-body { padding: 25px; }
.full-message { font-size: 1rem; line-height: 1.6; color: #333; white-space: pre-wrap; margin-bottom: 20px; }
.modal-actions { text-align: center; }
.btn-go-link { background: #3498db; color: white; border: none; padding: 10px 20px; border-radius: 25px; font-weight: bold; cursor: pointer; transition: background 0.2s; }
</style>